import { useEffect, useState } from "react";
import { TrendingUp, ShoppingBag, Store, Bike, Users } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { api, brl } from "@/lib/api";
import { useAdminTheme } from "./AdminLayout";
import { FilterBar, useAdminFilters, cityParam } from "./filters";

export default function AdminDashboard() {
  const { accent, isCity } = useAdminTheme();
  const f = useAdminFilters();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard", { params: { city_id: cityParam(f), period: f.period } })
      .then(({ data }) => setD(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.cityId, f.period]);

  const pie = d ? [
    { name: "Valor ao lojista", value: d.store_net, color: "#141414" },
    { name: "Repasse motoboy", value: d.courier_payout, color: "#2E8B57" },
    { name: "Prefeitura (3,5%)", value: d.municipal_fee, color: "#1F3864" },
    { name: "Time Food (2,5% + R$0,99)", value: d.platform_fee, color: "#FF7A1A" },
    { name: "Indicações (3,5% + 2,5%)", value: d.ref_fee, color: "#E53238" },
  ].filter((p) => p.value > 0) : [];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-gf-black">Dashboard geral</h1>
          <p className="text-sm text-gray-500">
            {isCity ? `Dados restritos ao município de ${f.cityName}/RJ` : "Consolidado de todas as cidades da plataforma"}
          </p>
        </div>
        <FilterBar f={f} accent={accent} />
      </div>

      {!d ? <p className="text-sm text-gray-500 mt-8">Carregando…</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6 gf-stagger">
            <Kpi testId="admin-kpi-gmv" icon={TrendingUp} label="Volume transacionado" value={brl(d.gmv)} accent={accent} highlight />
            <Kpi testId="admin-kpi-orders" icon={ShoppingBag} label="Pedidos" value={d.orders_count} accent={accent} />
            <Kpi testId="admin-kpi-stores" icon={Store} label="Lojistas ativos" value={d.active_stores} accent={accent} />
            <Kpi testId="admin-kpi-couriers" icon={Bike} label="Motoboys (online)" value={`${d.total_couriers} (${d.active_couriers})`} accent={accent} />
            <Kpi testId="admin-kpi-consumers" icon={Users} label="Consumidores" value={d.consumers} accent={accent} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mt-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <h2 className="font-display font-bold text-lg">Divisão de cada real transacionado</h2>
              <p className="text-xs text-gray-500">Taxa total da plataforma: 12% + R$ 0,99 de serviço por pedido</p>
              <div data-testid="admin-split-pie" className="mt-2">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pie} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={3}>
                      {pie.map((p) => <Cell key={p.name} fill={p.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => brl(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <h2 className="font-display font-bold text-lg">Resumo do período</h2>
              <div className="mt-4 space-y-3">
                <Line label="Ticket médio" value={brl(d.avg_ticket)} />
                <Line label="Valor líquido aos lojistas" value={brl(d.store_net)} color="#141414" />
                <Line label="Repasses a motoboys" value={brl(d.courier_payout)} color="#2E8B57" />
                <Line label="Taxa Prefeitura (3,5%)" value={brl(d.municipal_fee)} color="#1F3864" testId="admin-municipal-fee" />
                <Line label="Time Food (2,5% + serviço)" value={brl(d.platform_fee)} color="#FF7A1A" testId="admin-platform-fee" />
                <Line label="Comissões de indicação" value={brl(d.ref_fee)} color="#E53238" testId="admin-ref-fee" />
                <Line label="Taxa de serviço R$ 0,99 acumulada" value={brl(d.service_fee)} testId="admin-service-fee" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, accent, highlight, testId }) => (
  <div data-testid={testId} className="rounded-2xl border bg-white p-4" style={{ borderColor: highlight ? accent : "#E5E7EB" }}>
    <Icon size={17} style={{ color: highlight ? accent : "#9CA3AF" }} />
    <p className="text-[11px] text-gray-500 mt-2">{label}</p>
    <p className="font-display text-xl font-extrabold text-gf-black leading-tight">{value}</p>
  </div>
);

const Line = ({ label, value, color, testId }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="flex items-center gap-2 text-gray-600">
      {color && <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />}
      {label}
    </span>
    <span data-testid={testId} className="font-display font-extrabold">{value}</span>
  </div>
);
